import {
  Children,
  cloneElement,
  isValidElement,
  type ReactNode,
} from 'react';
import { MediaPosition } from '@/app/_components/shared/types';
import {
  getPositionClasses,
  type Breakpoint,
} from '@/app/_components/shared/utils/component-helpers';
import { cn } from '@/app/_lib/utils';

const BREAKPOINT_CLASSES: Record<Breakpoint, string> = {
  sm: 'sm:grid-cols-2',
  md: 'md:grid-cols-2',
  lg: 'lg:grid-cols-2',
  xl: 'xl:grid-cols-2',
  landscape: 'landscape:grid-cols-2',
};

interface TwoColumnLayoutProps {
  children: ReactNode;
  position?: MediaPosition;
  breakpoint?: Breakpoint;
  mobileLayout?: 'top' | 'bottom';
  className?: string;
}

// Props the root injects into <Left/> and <Right/> children via cloneElement.
// Optional so callers can render the columns without manually wiring layout.
interface InjectedColumnProps {
  _isRight?: boolean;
  _isBottom?: boolean;
  _breakpoint?: Breakpoint;
}

interface ColumnProps extends InjectedColumnProps {
  children: ReactNode;
  className?: string;
}

function TwoColumnLayoutRoot({
  children,
  position = MediaPosition.TOP_LEFT,
  breakpoint = 'lg',
  mobileLayout,
  className,
}: TwoColumnLayoutProps) {
  const isRight = position.includes('right');
  const isBottom =
    mobileLayout !== undefined
      ? mobileLayout === 'bottom'
      : position.includes('bottom');

  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-two-column-gap-mobile lg:gap-two-column-gap-desktop',
        BREAKPOINT_CLASSES[breakpoint],
        className,
      )}
    >
      {Children.map(children, (child) =>
        isValidElement<ColumnProps>(child)
          ? cloneElement(child, {
              _isRight: isRight,
              _isBottom: isBottom,
              _breakpoint: breakpoint,
            })
          : child,
      )}
    </div>
  );
}

function Left({
  children,
  className,
  _isRight = false,
  _isBottom = false,
  _breakpoint = 'lg',
}: ColumnProps) {
  const positionClasses = getPositionClasses(
    _isRight,
    _isBottom,
    true,
    _breakpoint,
  );
  return (
    <div className={cn('min-w-0', positionClasses, className)}>{children}</div>
  );
}

function Right({
  children,
  className,
  _isRight = false,
  _isBottom = false,
  _breakpoint = 'lg',
}: ColumnProps) {
  const positionClasses = getPositionClasses(
    _isRight,
    _isBottom,
    false,
    _breakpoint,
  );
  return (
    <div className={cn('min-w-0', positionClasses, className)}>{children}</div>
  );
}

export const TwoColumnLayout = Object.assign(TwoColumnLayoutRoot, {
  Left,
  Right,
});
